/* eslint-disable */
/* eslint-disable react/no-children-prop */
import React, { lazy, Suspense, useEffect, useState } from "react";
import { BrowserRouter, Redirect, Route, Switch } from "react-router-dom";
import { CssBaseline, ThemeProvider } from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import Layout from "@app/components/layout";
import PageLoader from "@app/components/page-loader";
import GlobalStyles from "@app/themes/global";
import baseTheme from "@app/themes/base";
import { getUserRole } from "./utils/role";
import TokenService from "@app/redux/services/token";
import ErrorBoundary from "./pages/errors/ErrorBoundary";
import { loadScript } from "@app/utils/CaptchaUtils";
import useGetFeatureSettings from "./hooks/use-get-feature-settings/useGetFeatureSettings";
import useGetAppstoreVersion from "./hooks/use-get-appstore-version/useGetAppstoreVersion";
import { AuthModel } from "./redux/models";
import { RootState } from "./redux/store/root";
import { fetchUserData } from "./redux/actions/auth";
import useFeatureToggle from "@app/hooks/use-feature-toggle/useFeatureToggle";
import { Feature } from "@app/enums/Feature";

/* Pages */
const PageNotFound = lazy(() => import("@app/pages/errors/pageNotFound"));
const DeveloperPage = lazy(() => import("@app/pages/developer"));
const DeveloperAppListPage = lazy(() => import("@app/pages/developer/list"));
const DeveloperAppEditPage = lazy(() => import("@app/pages/developer/edit"));
const DeveloperAnalyticsPage = lazy(() => import("@app/pages/developer/analytics"));
const OemAnalyticsPage = lazy(() => import("@app/pages/oem/analytics"));
const OemAIPage = lazy(() => import("@app/pages/oem/ai"));
const OemPage = lazy(() => import("@app/pages/oem"));
const OemAppListPage = lazy(() => import("@app/pages/oem/list"));
const LoginPage = lazy(() => import("@app/pages/login"));
const ForgotPassword = lazy(() => import("@app/pages/forgotPassword"));
const ForgotPasswordReset = lazy(() => import("@app/pages/forgotPassword"));
const accountVerificationPage = lazy(() => import("@app/pages/accountVerification"));
const IVUserRegistrationForm = lazy(() => import("@app/pages/iv-user-registration"));
const RegisterPage = lazy(() => import("@app/pages/register"));
const OemAppEditPage = lazy(() => import("@app/pages/oem/edit"));
const VehiclesPage = lazy(() => import("@app/pages/vehicles"));
const TesterPage = lazy(() => import("@app/pages/tester"));
const TesterAppListPage = lazy(() => import("@app/pages/tester/list"));
const TesterAppEditPage = lazy(() => import("@app/pages/tester/edit"));
const ProfilePage = lazy(() => import("@app/pages/profile"));
const RecoverPassword = lazy(() => import("@app/pages/recoverPassword"));
const ChangePassword = lazy(() => import("@app/pages/changePassword"));
const Feedback = lazy(() => import("@app/pages/feedback"));

/* Sub Header Components */
const DeveloperSubHeader = lazy(() => import("@app/components/subHeader/developer"));
const AnalyticsSubHeader = lazy(() => import("@app/components/subHeader/developerAnalytics"));
const OemAnalyticsSubHeader = lazy(() => import("@app/components/subHeader/oemAnalytics"));
const OemAISubHeader = lazy(() => import("@app/components/subHeader/oemAi"));
const DeveloperDetailsSubHeader = lazy(() => import("@app/components/subHeader/developerDetails"));
const OemSubHeader = lazy(() => import("@app/components/subHeader/oem"));
const OemDetailSubHeader = lazy(() => import("@app/components/subHeader/oemDetail"));
const VehiclesSubHeader = lazy(() => import("@app/components/subHeader/vehicles"));
const TesterSubHeader = lazy(() => import("@app/components/subHeader/tester"));
const TesterDetailSubHeader = lazy(() => import("@app/components/subHeader/testerDetail"));
const ProfileSubHeader = lazy(() => import("@app/components/subHeader/profile"));

import "../public/fonts/VWTextCarUI-Regular.ttf";
import "../public/fonts/VWTextCarUI-Light.ttf";
import "../public/fonts/VWTextCarUI-Bold.ttf";
import useStyles from "./styles";

interface RouteMap {
    [key: string]: string;
}

/**
 * Mapping of routes with roles, for eg. oem role has access to vehicles routes
 * It is used in header component to find out the role based on routes
 */
const getRole = () => {
    try {
        const user = TokenService.decodeJWT();
        return getUserRole(user);
    } catch (err) {
        return "";
    }
};
export const routePortalMap: RouteMap = {
    developer: () => "dev",
    oem: () => "oem",
    vehicles: () => "oem",
    tester: () => "tester",
    profile: () => getRole(),
};

const RoutingComponent = React.memo(() => {
    const isAiChatbotEnabled = useFeatureToggle(Feature.AI_CHATBOT, false);

    return (
        <Switch>
            <Redirect exact from="/" to="/login" />
            <Route exact path="/login" component={LoginPage} />
            <Route exact path="/forgot-password/" component={ForgotPassword} />
            <Route exact path="/forgot-password/:token" component={ForgotPasswordReset} />
            <Route exact path="/verification/:id" component={accountVerificationPage} />
            <Route exact path="/ivuserregistration" component={IVUserRegistrationForm} />
            <Route exact path="/recover-password/:token" component={RecoverPassword} />
            <Route exact path="/change-password/:token" component={ChangePassword} />
            <Route exact path="/feedback/:token/:requestType" component={Feedback} />
            <Route exact path="/:role/register" component={RegisterPage} />
            <Route>
                <Switch>
                    {/* Layout is the private route where user is logged or not is checked */}
                    <Layout exact path="/developer" component={DeveloperPage} />
                    <Layout
                        exact
                        path="/developer/apps"
                        component={DeveloperAppListPage}
                        subheader={DeveloperSubHeader}
                    />
                    <Layout
                        exact
                        path="/developer/analytics"
                        component={DeveloperAnalyticsPage}
                        subheader={AnalyticsSubHeader}
                    />
                    <Layout
                        exact
                        path="/oem/analytics"
                        component={OemAnalyticsPage}
                        subheader={OemAnalyticsSubHeader}
                    />
                    {isAiChatbotEnabled && (
                        <Layout
                            exact
                            path="/oem/ai"
                            component={OemAIPage}
                            subheader={OemAISubHeader}
                            hideFooter={true}
                        />
                    )}
                    {isAiChatbotEnabled && (
                        <Layout
                            exact
                            path="/oem/ai-demo"
                            component={OemAIPage}
                            subheader={OemAISubHeader}
                            hideFooter={true}
                        />
                    )}
                    <Layout
                        exact
                        path="/developer/apps/:id"
                        component={DeveloperAppEditPage}
                        subheader={DeveloperDetailsSubHeader}
                    />
                    <Layout exact path="/oem" component={OemPage} />
                    <Layout
                        exact
                        path="/oem/apps"
                        component={OemAppListPage}
                        subheader={OemSubHeader}
                    />
                    <Layout
                        exact
                        path="/oem/apps/:id"
                        component={OemAppEditPage}
                        subheader={OemDetailSubHeader}
                    />
                    <Layout
                        exact
                        path="/vehicles"
                        component={VehiclesPage}
                        subheader={VehiclesSubHeader}
                    />
                    <Layout
                        exact
                        path="/tester"
                        component={TesterPage}
                        subheader={TesterSubHeader}
                    />
                    <Layout
                        exact
                        path="/tester/apps"
                        component={TesterAppListPage}
                        subheader={TesterSubHeader}
                    />
                    <Layout
                        exact
                        path="/tester/apps/:id"
                        component={TesterAppEditPage}
                        subheader={TesterDetailSubHeader}
                    />
                    <Layout
                        exact
                        path="/profile"
                        component={ProfilePage}
                        subheader={ProfileSubHeader}
                    />
                    <Route path="*" component={PageNotFound} />
                </Switch>
            </Route>
        </Switch>
    );
});

function App(): JSX.Element {
    useStyles();
    const dispatch = useDispatch();
    const authDetails: AuthModel = useSelector((state: RootState) => state.authDetails);
    const [loading, setLoading] = useState(true);
    const { isLoggedIn } = authDetails;

    useGetFeatureSettings();
    useGetAppstoreVersion(isLoggedIn);

    useEffect(() => {
        const loadUserData = async () => {
            if (authDetails?.isLoggedIn) {
                await dispatch(fetchUserData());
            }
            setLoading(false);
        };
        loadUserData();
    }, [authDetails?.isLoggedIn]);

    useEffect(() => {
        loadScript();
    }, []);

    if (loading) {
        return <PageLoader />;
    }
    return (
        <ThemeProvider theme={baseTheme}>
            <CssBaseline />
            <GlobalStyles />
            <Suspense fallback={<PageLoader />}>
                <BrowserRouter>
                    <ErrorBoundary>
                        <RoutingComponent />
                    </ErrorBoundary>
                </BrowserRouter>
            </Suspense>
        </ThemeProvider>
    );
}

export default App;
